import * as Sentry from '@sentry/react';

// 0559: prioridad versión semver (__APP_VERSION__ inyectado por vite.config.ts desde
// package.json) sobre SHA. Tras semver-bump del 0551, package.json refleja la versión
// semántica (v1.1.0, v1.2.0, ...). El SHA queda como fallback durante development.
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null;
const GIT_SHA = (import.meta.env.VITE_GIT_SHA as string | undefined) ?? null;
const RELEASE = APP_VERSION ?? GIT_SHA ?? 'dev';

/**
 * Origenes que Sentry traceara como peticiones de la app. Evita anadir
 * terceros (CDNs, analytics) que no forman parte del perimetro de
 * responsabilidad del producto.
 */
const TRACING_ORIGINS: Array<string | RegExp> = [
  'localhost',
  /^\//, // same-origin (produccion: vercel domain)
  /^https:\/\/api\.valoriagovernance\.com/,
];

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: RELEASE,
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    tracePropagationTargets: TRACING_ORIGINS,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
  });
}

export interface SentryUserPayload {
  id: string;
  tenantId: string;
  email?: string;
}

/**
 * Enriquece los eventos Sentry con user + tenant.
 * Llamar desde AuthContext cuando se resuelva el usuario autenticado.
 */
export function setSentryUser(user: SentryUserPayload) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.setUser({ id: user.id, email: user.email });
  Sentry.setTag('tenantId', user.tenantId);
}

/**
 * Limpia el scope de usuario — usar al logout.
 */
export function clearSentryUser() {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.setUser(null);
}

export { Sentry, RELEASE as SENTRY_RELEASE };
